import React, { ReactElement, ReactNode, useState, useRef, useEffect } from 'react';
import { InformationCircleIcon } from '@heroicons/react/solid';
import { tv } from 'tailwind-variants';

export interface TooltipProperties {
  text: ReactElement;
  link?: ReactNode;
  title?: string;
  className?: string;
  onClick?: () => void;
}

const tooltip = tv({
  base: 'tooltip__content relative inline-block cursor-pointer',
});

const TooltipInfo = ({ text, link, title, className, onClick }: TooltipProperties) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const handleClick = () => {
    setOpen((v) => !v);
    onClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((v) => !v);
      onClick?.();
    }
  };

  return (
    <button
      ref={ref}
      type="button"
      className={tooltip({ className })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-expanded={open}
      aria-haspopup="true"
    >
      {title ? (
        <span className="text-xs font-semibold text-primary">{title}</span>
      ) : (
        <InformationCircleIcon className="w-[1.3rem] h-[1.3rem] block text-neutral-dark" />
      )}
      {open && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 rounded-lg shadow-xl bg-primary text-neutral-lightest p-[18px] text-xs z-50">
          <span>{text}</span>
          {link}
        </div>
      )}
    </button>
  );
};

export default TooltipInfo;
